import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { extractFromLocalizedString, LocalizedString } from '@examdojo/core/i18n';
import { SupabaseSelectQueryConfig } from '@examdojo/core/supabase';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { calculateStemTotalMarks, SubStem } from '@examdojo/models/markscheme';
import {
  getOrderedItemTagValue,
  QuestionHttpModel,
  QuestionImageHttpModel,
  QuestionVoteStoreModel,
  StemHttpModel,
  StimulusHttpModel,
  StimulusStoreModel,
} from '@examdojo/models/question';
import { TableUpdateModel } from '@examdojo/supabase';
import { sanitizeObject } from '@examdojo/util/sanitize-object';
import omit from 'lodash/omit';
import { EMPTY, finalize, forkJoin, from, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  QUESTION_UPDATE_ALLOWED_KEYS,
  QuestionEntityStoreModel,
  QuestionItemQuestionImageStoreModel,
  QuestionItemV1,
  QuestionStemStoreModel,
  QuestionStoreModel,
} from './models';
import { QuestionEntityHttpService } from './question-http.service';
import { Feedback } from './question-user-feedback';
import { mapDerivativeQuestionsHttpModelToStoreModel } from './question.mapper';
import { QuestionEntityQuery } from './question.query';
import { QuestionEntityStore } from './question.store';

@Injectable({ providedIn: 'root' })
export class QuestionEntityService {
  constructor(
    private readonly questionHttpService: QuestionEntityHttpService,
    private readonly store: QuestionEntityStore,
    private readonly query: QuestionEntityQuery,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  fetchQuestionDetails(
    questionId: QuestionHttpModel['id'] | null = this.query.getActiveId(),
    {
      setActive = false,
      resetStore = false,
    }: {
      setActive?: boolean;
      resetStore?: boolean;
    },
  ) {
    if (!questionId) {
      return of(null);
    }

    if (resetStore) {
      this.resetStore();
    }

    this.store.setLoading(true);

    return this.fetch(questionId).pipe(
      switchMap((question) => {
        return forkJoin([this.fetchQuestionItems(questionId), this.fetchQuestionVote(questionId)]).pipe(
          map(([items, vote]) => {
            if (!question) {
              return;
            }

            return {
              question,
              id: question.id,
              items,
              userVote: vote,
            };
          }),
          tap((questionStoreModel) => {
            if (!questionStoreModel) {
              return;
            }

            this.store.upsert(questionStoreModel.id, questionStoreModel);

            if (setActive) {
              this.store.setActive(questionStoreModel.id);
            }
          }),
          finalize(() => this.store.setLoading(false)),
        );
      }),
    );
  }

  resetActiveQuestion() {
    this.store.setActive(null);
  }

  resetStore() {
    this.store.reset();
  }

  fetch(questionId: QuestionHttpModel['id'] | null = this.query.getActiveId()): Observable<QuestionStoreModel | null> {
    if (!questionId) {
      return of(null);
    }

    return this.questionHttpService.fetch(questionId).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question', context: { questionId } }),
      map((question) => this.mapQuestionHttpModelToStoreModel(question)),
    );
  }

  fetchByIds(questionIds: Array<QuestionHttpModel['id']>): Observable<QuestionStoreModel[]> {
    if (!questionIds.length) {
      return of([] as QuestionStoreModel[]);
    }

    return this.questionHttpService.fetchByIds(questionIds).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question', context: { questionIds } }),
      map((questions) => questions.map((question) => mapDerivativeQuestionsHttpModelToStoreModel(question))),
    );
  }

  update(id: QuestionStoreModel['id'], model: Partial<QuestionStoreModel>) {
    const httpModel = this.mapUpdateModelToHttpUpdateModel(model);

    return this.questionHttpService.update(id, httpModel).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'examdojo.entity.question',
        action: 'update',
        context: { questionId: id, payload: model },
      }),
      tap((data) => {
        this.store.update(id, (entity) => ({ ...entity, question: this.mapQuestionHttpModelToStoreModel(data) }));
      }),
    );
  }

  delete(questionId: QuestionHttpModel['id']) {
    return this.questionHttpService.delete(questionId).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'examdojo.entity.question',
        action: 'delete',
        context: { questionId },
      }),
      tap(() => this.store.update(questionId, (entity) => ({ ...entity, status: 'DELETED' }))),
    );
  }

  fetchPaginatedListView(selectQueryConfig: SupabaseSelectQueryConfig = {}) {
    return this.questionHttpService
      .fetchPaginatedListView(selectQueryConfig)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question', action: 'fetch' }));
  }

  fetchQuestionVote(questionId: QuestionHttpModel['id'] | null = this.query.getActiveId()) {
    if (!questionId) {
      return of(null);
    }
    return this.questionHttpService
      .fetchQuestionVote(questionId)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question_vote' }));
  }

  setQuestionVote(
    options: { vote: boolean; problems?: Feedback[] | string[]; textFeedback?: string },
    questionId: QuestionHttpModel['id'] | null = this.query.getActiveId(),
  ) {
    if (!questionId) {
      return of(null);
    }

    return this.questionHttpService.setQuestionVote(questionId, options).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'examdojo.entity.question_vote',
        action: 'update',
        context: { questionId },
      }),
      switchMap(() => this.fetchQuestionVote(questionId)),
      tap((questionVote) => this.store.update(questionId, (entity) => ({ ...entity, userVote: questionVote ?? null }))),
    );
  }

  removeQuestionVote(
    voteId: QuestionVoteStoreModel['id'],
    questionId: QuestionHttpModel['id'] | null = this.query.getActiveId(),
  ) {
    if (!questionId) {
      return EMPTY;
    }

    return this.questionHttpService.removeQuestionVote(voteId).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'examdojo.entity.question_vote',
        action: 'delete',
        context: { questionId },
      }),
      tap(() => this.store.update(questionId, (entity) => ({ ...entity, userVote: null }))),
    );
  }
  /**
   * @deprecated Use fetchQuestionItems instead
   */
  fetchQuestionItemsByIds(questionIds: Array<QuestionHttpModel['id']>): Observable<QuestionItemV1[]> {
    if (!questionIds.length) {
      return of([]);
    }

    return forkJoin(questionIds.map((questionId) => this.fetchQuestionItems(questionId))).pipe(
      map((results) => results.flat()),
    );
  }

  fetchQuestionItems(
    questionId: QuestionHttpModel['id'] | null = this.query.getActiveId(),
  ): Observable<QuestionItemV1[]> {
    if (!questionId) {
      return of([]);
    }

    return this.questionHttpService.fetchItems(questionId).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'examdojo.entity.question_details',
        context: { questionId },
      }),
      map((items): NonNullable<QuestionEntityStoreModel['items']> => {
        if (!items) {
          return items;
        }

        const stemIdToIndexMap = items
          .filter((item) => item.item_type === 'stem')
          .reduce((stemToIndexMap, item, index) => {
            stemToIndexMap.set(item.item_id, index);
            return stemToIndexMap;
          }, new Map<number, number>());

        return items.map((item) => {
          if (item.item_type === 'stimulus') {
            return this.mapStimulusHttpModelToStoreModel({ ...omit(item, ['item_id']), id: item.item_id });
          } else if (item.item_type === 'stem') {
            return this.mapStemHttpModelToStoreModel(
              { ...omit(item, ['item_id']), id: item.item_id },
              stemIdToIndexMap.get(item.item_id) ?? 0,
            );
          } else {
            return this.mapQuestionImageHttpModelToStoreModel({ ...omit(item, ['item_id']), id: item.item_id });
          }
        });
      }),
      switchMap((items) => {
        const questionImageItems = items.filter(
          (item): item is QuestionItemQuestionImageStoreModel => item.type === 'question_image',
        );

        const questionImageItemsWithNames = questionImageItems.filter((item) => !!item.question_image_name);

        const questionImageItemBucketName = questionImageItemsWithNames[0]?.question_image_bucket; // all question image items have the same bucket

        if (!questionImageItemsWithNames.length || !questionImageItemBucketName) {
          return of(items);
        }

        const questionImageNames = questionImageItemsWithNames.map((item) => item.question_image_name);

        return from(this.fetchSignedUrlsForImage(questionImageNames, questionImageItemBucketName)).pipe(
          map((signedUrls) =>
            items.map((item) => {
              if (item.type === 'question_image') {
                item.questionImageUrl = item.question_image_name ? signedUrls[item.question_image_name] : null;
              }

              return item;
            }),
          ),
        );
      }),
    );
  }

  fetchSyllabusIdByQuestionId(questionId: QuestionEntityStoreModel['id']): Observable<number | null> {
    return this.questionHttpService
      .fetchSyllabusIdByQuestionId(questionId)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question', action: 'fetch' }));
  }

  fetchQuestionStatus(questionId: QuestionEntityStoreModel['id']): Observable<string | null> {
    return this.questionHttpService
      .fetchQuestionStatus(questionId)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.question', action: 'fetch' }));
  }

  createSeedQuestion(questionId: QuestionEntityStoreModel['id']): Observable<number | null> {
    return this.questionHttpService
      .createSeedQuestion(questionId)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.seed', action: 'create' }));
  }

  private async fetchSignedUrlsForImage(imageNames: Array<string | null>, bucketName: string) {
    const data = await this.questionHttpService.fetchSignedUrls(imageNames.filter(isNotNullish), bucketName);

    return data.reduce<Record<string, string | null>>((acc, item) => {
      return {
        ...acc,
        ...(item.path ? { [item.path]: item.error ? null : item.signedUrl } : {}),
      };
    }, {});
  }

  private mapQuestionHttpModelToStoreModel(questionHttpModel: QuestionHttpModel): QuestionStoreModel {
    return {
      ...omit(questionHttpModel, ['author']),
      authorId: questionHttpModel.author,
      items: questionHttpModel.items as QuestionStoreModel['items'],
      llm_feedback: questionHttpModel.llm_feedback as unknown as QuestionStoreModel['llm_feedback'],
      question_items: questionHttpModel.question_items as unknown as QuestionStoreModel['question_items'],
      metadata: questionHttpModel.metadata as QuestionStoreModel['metadata'],
    };
  }

  private mapStimulusHttpModelToStoreModel(httpModel: StimulusHttpModel): StimulusStoreModel {
    return {
      ...omit(httpModel, ['author']),
      authorId: httpModel.author!,
      stimulus: extractFromLocalizedString(httpModel.stimulus),
      type: 'stimulus',
    };
  }

  private mapStemHttpModelToStoreModel(httpModel: StemHttpModel, stemIndex: number): QuestionStemStoreModel {
    return {
      ...omit(httpModel, ['author', 'markscheme']),
      authorId: httpModel.author!,
      stem: (httpModel.stem as Array<LocalizedString | string>).map(extractFromLocalizedString),
      type: 'stem',
      markscheme: httpModel.markscheme as SubStem[] | null,
      totalMarks: httpModel.markscheme ? calculateStemTotalMarks(httpModel.markscheme as unknown as SubStem[]) : 0,
      stemIndex,
      label: getOrderedItemTagValue(stemIndex),
    };
  }

  private mapQuestionImageHttpModelToStoreModel(
    httpModel: QuestionImageHttpModel,
  ): QuestionItemQuestionImageStoreModel {
    return {
      ...omit(httpModel, ['author']),
      authorId: httpModel.author!,
      description: extractFromLocalizedString(httpModel.description),
      type: 'question_image',
      questionImageUrl: null,
    };
  }

  private mapUpdateModelToHttpUpdateModel(updateModel: Partial<QuestionStoreModel>): TableUpdateModel<'questions'> {
    const sanitized = sanitizeObject(updateModel, QUESTION_UPDATE_ALLOWED_KEYS);

    return {
      ...sanitized,
      llm_feedback: sanitized.llm_feedback as unknown as QuestionHttpModel['llm_feedback'],
      question_items: sanitized.question_items as unknown as QuestionHttpModel['question_items'],
      metadata: sanitized.metadata as unknown as QuestionHttpModel['metadata'],
    };
  }
}
